import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Loader2 } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { api } from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/blog")
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="grain relative min-h-screen">
      <Helmet>
        <title>Blog — Avis Google, NFC & SEO local | GMP Développement</title>
        <meta
          name="description"
          content="Conseils, guides et tendances sur les avis Google, les chevalets NFC, les menus digitaux QR code et le référencement local. Le blog de GMP Développement."
        />
        <link rel="canonical" href={typeof window !== "undefined" ? window.location.origin + "/blog" : "/blog"} />
        <meta property="og:title" content="Blog GMP Développement — Avis Google & visibilité locale" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 pt-36 pb-24 relative z-10">
        <header className="max-w-3xl mb-16">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-warm mb-4">Le blog</p>
          <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
            Visibilité locale & avis Google
          </h1>
          <p className="mt-6 text-muted-foreground text-base sm:text-lg leading-relaxed">
            Nos guides et analyses pour transformer votre présence en ligne en
            véritable moteur de croissance.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <p data-testid="blog-empty" className="text-muted-foreground py-16">Aucun article pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-list">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
              >
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${i}`}
                  className="group block bg-surface border border-border overflow-hidden hover:border-warm/50 transition-colors h-full"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={p.cover_image}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      {p.tags.slice(0, 2).map((t) => (
                        <span key={t} className="font-mono text-[9px] tracking-wider uppercase text-warm border border-warm/30 px-2 py-0.5">
                          {t}
                        </span>
                      ))}
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock size={12} /> {p.reading_time} min
                      </span>
                    </div>
                    <h2 className="font-heading font-semibold text-xl leading-snug mb-2 group-hover:text-warm transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-sm text-accent">
                      Lire l'article <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
